import Api, { make, Response } from 'src/services/api'
import isThisType from 'src/helpers/is-this-ype'

import {
  Id,
  PolicyApprovalExceptionTrees,
  PolicyApprovalRules,
  PolicyConferenceExceptionTrees,
  PolicyConferenceRules,
  PolicyFlowApproval,
  PolicyFlowClosing,
  PolicyFlowConference
} from 'src/types'

/// Policy flow Approvals
type PolicyApprovalsResponse = {
  policy_approvals: PolicyFlowApproval[]
}

export function getPolicyApprovals(): Promise<
  Response<PolicyApprovalsResponse>
> {
  return make<PolicyApprovalsResponse>({
    axiosCall: Api.get('policy_approvals')
  })
}

export function SavePolicyApprovals(
  record: PolicyFlowApproval
): Promise<Response<PolicyApprovalsResponse>> {
  return make<PolicyApprovalsResponse>({
    axiosCall: Api.post('policy_approvals', {
      policy_approvals: [record]
    })
  })
}

/// Policy flow Conference
type PolicyConferencesResponse = {
  policy_conferences: PolicyFlowConference[]
}

export function getPolicyConferences(): Promise<
  Response<PolicyConferencesResponse>
> {
  return make<PolicyConferencesResponse>({
    axiosCall: Api.get('policy_conferences')
  })
}

export function SavePolicyConferences(
  record: PolicyFlowConference
): Promise<Response<PolicyConferencesResponse>> {
  return make<PolicyConferencesResponse>({
    axiosCall: Api.post('policy_conferences', {
      policy_conferences: [record]
    })
  })
}

/// Policy flow Closings
type PolicyClosingsResponse = {
  policy_closings: PolicyFlowClosing[]
}

export function getPolicyClosings(): Promise<Response<PolicyClosingsResponse>> {
  return make<PolicyClosingsResponse>({
    axiosCall: Api.get('policy_closings')
  })
}

export function SavePolicyClosings(
  record: PolicyFlowClosing
): Promise<Response<PolicyClosingsResponse>> {
  return make<PolicyClosingsResponse>({
    axiosCall: Api.post('policy_closings', {
      policy_closings: [record]
    })
  })
}

// =============== RULES ================= //

type GetRulesExceptionProps = {
  organization_id: Id
  policy_name: 'approval' | 'conference'
  policy_id: Id
}

type SaveRulesExceptionProps = GetRulesExceptionProps & {
  policy_approval_rules?: PolicyApprovalRules[]
  policy_conference_rules?: PolicyConferenceRules[]
}

type Data<T> = {
  [key: string]: T
}

export function getRulesException({
  organization_id,
  policy_name,
  policy_id
}: GetRulesExceptionProps): Promise<
  Response<PolicyApprovalRules[] | PolicyConferenceRules[]>
> {
  const policyName =
    policy_name === 'approval'
      ? 'policy_approval_rules'
      : 'policy_conference_rules'
  return make<Data<any>, PolicyApprovalRules[] | PolicyConferenceRules[]>({
    axiosCall: Api.get(
      `organizations/${organization_id}/policy/${policy_name}/${policy_id}/rules`
    ),
    transformData: data => data[policyName]
  })
}

export function SaveRulesException({
  organization_id,
  policy_approval_rules,
  policy_conference_rules,
  policy_id,
  policy_name
}: SaveRulesExceptionProps): Promise<
  Response<PolicyApprovalRules[] | PolicyConferenceRules[]>
> {
  const policy_rules =
    policy_name === 'approval'
      ? {
          policy_approval_rules
        }
      : { policy_conference_rules }
  return make<any, PolicyApprovalRules[] | PolicyConferenceRules[]>({
    axiosCall: Api.post(
      `organizations/${organization_id}/policy/${policy_name}/${policy_id}/rules`,
      policy_rules
    ),
    transformData: data =>
      data[
        policy_name === 'approval'
          ? 'policy_approval_rules'
          : 'policy_conference_rules'
      ]
  })
}

// =============== ExceptionTrees ================= //

export interface ExceptionTreeResponse {
  policy_exception_trees:
    | PolicyApprovalExceptionTrees[]
    | PolicyConferenceExceptionTrees[]
}

export function saveExceptionTree(
  policy_exception_trees:
    | PolicyApprovalExceptionTrees
    | PolicyConferenceExceptionTrees
): Promise<Response<ExceptionTreeResponse>> {
  const policy = JSON.parse(JSON.stringify(policy_exception_trees))
  delete policy.tree_exception_tree_name

  if (
    isThisType<PolicyApprovalExceptionTrees>(
      policy_exception_trees,
      'policy_approval_rule_id'
    )
  ) {
    const policy_approval_rule_id = policy.policy_approval_rule_id || ''
    return make<ExceptionTreeResponse>({
      axiosCall: Api.post(
        `/policies/approval/rule/${policy_approval_rule_id}/exception/trees`,
        { policy_approval_exception_trees: [policy] }
      )
    })
  } else {
    const policy_conference_rule_id = policy.policy_conference_rule_id || ''
    return make<ExceptionTreeResponse>({
      axiosCall: Api.post(
        `/policies/conference/rule/${policy_conference_rule_id}/exception/trees`,
        { policy_conference_exception_trees: [policy] }
      )
    })
  }
}
